import React, { useCallback, useEffect, useRef, useState } from 'react';
import { KeyRound, LoaderCircle, ShieldCheck, X } from 'lucide-react';
import { getApiKeyStatus, profileErrorMessage, removeApiKey, saveApiKey } from '../services/profileService';
import ProfileTokenBalance from './ProfileTokenBalance';

function ProfileApiKeySettings({ provider, onBusyChange }) {
  const [apiKey, setApiKey] = useState('');
  const [configured, setConfigured] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [attempt, setAttempt] = useState(0);
  const providerName = provider === 'gemini' ? 'Gemini' : 'OpenAI';

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    (provider === 'gemini' ? getApiKeyStatus('gemini') : getApiKeyStatus()).then((status) => {
      if (active) setConfigured(status.configured);
    }).catch((failure) => {
      if (active) setError(profileErrorMessage(failure));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [attempt, provider]);

  async function updateKey(action) {
    if (busy) return;
    setBusy(true);
    onBusyChange(provider, true);
    setError('');
    setMessage('');
    try {
      const status = action === 'remove'
        ? await (provider === 'gemini' ? removeApiKey('gemini') : removeApiKey())
        : await (provider === 'gemini' ? saveApiKey(apiKey.trim(), 'gemini') : saveApiKey(apiKey.trim()));
      setConfigured(status.configured);
      setApiKey('');
      setMessage(action === 'remove' ? 'API key removed.' : provider === 'gemini' ? 'Gemini API key saved. It will be used for your Gemini image and video requests.' : 'API key saved. It will be used for your image requests.');
    } catch (failure) { setError(profileErrorMessage(failure)); }
    finally { setBusy(false); onBusyChange(provider, false); }
  }
  return (
    <section className="profile-api-section" aria-labelledby={provider + '-api-key-title'}>
      <div className="profile-section-title"><KeyRound size={19} /><h3 id={provider + '-api-key-title'}>{providerName} {provider === 'gemini' ? 'for images and video' : 'for images and HTML'}</h3></div>
      <p>{provider === 'gemini' ? 'Add a Gemini API key for image generation with Nano Banana 2.1 and video generation with Gemini Omni Flash. Choose Gemini in AI Design Studio for images. Usage is billed to your Google account.' : 'Add an OpenAI API key for image generation and editing. Usage is billed to your OpenAI account.'}</p>
      {loading ? <p className="profile-key-status" role="status"><LoaderCircle size={15} className="spin" />Checking API key...</p>
        : <p className="profile-key-status" role="status"><span className={configured ? 'profile-status-dot connected' : 'profile-status-dot'} />{configured === null ? providerName + ' key status unavailable' : configured ? providerName + ' API key saved' : 'No ' + providerName + ' API key saved'}</p>}
      <form onSubmit={(event) => { event.preventDefault(); updateKey('save'); }}>
        <label htmlFor={provider + '-api-key'}>{(configured ? 'Replace ' : '') + providerName + ' API key'}</label>
        <input id={provider + '-api-key'} name={provider + '-api-key'} type="password" autoComplete="new-password" spellCheck={false}
          placeholder={provider === 'gemini' ? 'Paste your Gemini key' : 'sk-...'} value={apiKey} onChange={(event) => setApiKey(event.target.value)} minLength={provider === 'gemini' ? undefined : 19} maxLength={provider === 'gemini' ? 8192 : 512}
          required disabled={loading || busy} aria-describedby={provider + '-key-privacy'} />
        <p id={provider + '-key-privacy'} className="profile-key-privacy"><ShieldCheck size={15} />Your key is stored encrypted and is never shown again.</p>
        {provider === 'gemini' && <p className="profile-key-privacy">Google checks key validity and access when you generate. Saving a key does not verify it.</p>}
        {error && <div className="profile-feedback error" role="alert">{error}<button type="button" onClick={() => setAttempt((value) => value + 1)} disabled={busy || loading}>Refresh status</button></div>}
        {message && <p className="profile-feedback success" role="status">{message}</p>}
        <div className="profile-actions">
          {configured && <button type="button" className="profile-remove" disabled={busy || loading} onClick={() => updateKey('remove')}>Remove {providerName} key</button>}
          <button type="submit" className="profile-save" disabled={loading || busy || !apiKey.trim()}>{busy && <LoaderCircle size={15} className="spin" />}{busy ? 'Updating...' : (configured ? 'Replace ' : 'Save ') + providerName + ' key'}</button>
        </div>
      </form>
    </section>
  );
}

export default function ProfileModal({ user, onClose }) {
  const dialogRef = useRef(null);
  const [busyProviders, setBusyProviders] = useState({});
  const busy = Object.values(busyProviders).some(Boolean);
  const onBusyChange = useCallback((provider, pending) => {
    setBusyProviders(current => ({ ...current, [provider]: pending }));
  }, []);
  useEffect(() => {
    const dialog = dialogRef.current;
    dialog.showModal();
    return () => dialog.close();
  }, []);
  const providers = [...new Set((user.providerData || []).map((provider) => provider.providerId === 'google.com' ? 'Google' : provider.providerId === 'password' ? 'Email and password' : provider.providerId))].join(', ');
  const created = user.metadata?.creationTime ? new Date(user.metadata.creationTime) : null;
  return <dialog ref={dialogRef} className="profile-dialog canvas-panel-scroll" aria-labelledby="profile-title" aria-describedby="profile-description"
    onCancel={(event) => { event.preventDefault(); if (!busy) onClose(); }}>
    <header className="profile-dialog-header"><div><span className="profile-kicker">YOUR ACCOUNT</span><h2 id="profile-title">Profile &amp; settings</h2></div>
      <button type="button" className="profile-close" onClick={onClose} disabled={busy} aria-label="Close profile"><X size={20} /></button>
    </header>
    <p id="profile-description" className="profile-description">Your details and AI generation settings.</p>
    <dl className="profile-account-details">
      <div><dt>Name</dt><dd>{user.displayName || 'Not provided'}</dd></div>
      <div><dt>Email</dt><dd>{user.email || 'Not provided'}</dd></div>
      {providers && <div><dt>Sign-in method</dt><dd>{providers}</dd></div>}
      {created && !Number.isNaN(created.getTime()) && <div><dt>Member since</dt><dd>{created.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</dd></div>}
    </dl>
    <ProfileTokenBalance uid={user.uid} />
    <ProfileApiKeySettings key={'openai-' + user.uid} provider="openai" onBusyChange={onBusyChange} />
    <ProfileApiKeySettings key={'gemini-' + user.uid} provider="gemini" onBusyChange={onBusyChange} />
  </dialog>;
}
